import { useMemo, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { format } from 'date-fns';
import PoolEventPrize from '../../components/popups/PoolEventPrize';
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow_left.svg';
import TriangleLeft from '../../assets/images/pool_event/triangle-left.png';
import TriangleRight from '../../assets/images/pool_event/triangle-right.png';
import Crown1 from '../../assets/images/pool_event/crown-1.png';
import Crown2 from '../../assets/images/pool_event/crown-2.png';
import Crown3 from '../../assets/images/pool_event/crown-3.png';
import Grid2x2 from '../../assets/images/pool_event/grid2x2.png';
import NotYetCat from '../../assets/images/pool_event/not_yet_cat.png';
import ToastAlert from '../../components/common/Toast';

export default function PrizeWinner() {
  const navigate = useNavigate();
  const [isNoticeVisible, setIsNoticeVisible] = useState(true);
  const [currentRound, setCurrentRound] = useState(1);
  const [message, setMessage] = useState(false);
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken } = useContext(AuthContext);
  const accessToken = useAccessToken();

  const { data: eventInfo } = useSWR(`${API_SERVER_DOMAIN}/pool/event/info`, (url) =>
    fetch(url).then((res) => res.json()),
  );
  const { data: winnerList } = useSWR(
    eventInfo &&
      accessToken && [`${API_SERVER_DOMAIN}/pool/event/result?eventNo=1&eventRoundNo=${currentRound}`, accessToken],
    fetchWithToken,
  );

  useEffect(() => {
    // 현재 라운드 설정
    if (eventInfo?.infos?.eventRoundList) {
      const activeRound = eventInfo.infos.defaultShowResult;
      if (activeRound) {
        setCurrentRound(activeRound.round);
      }
    }
  }, [eventInfo]);

  useEffect(() => {
    // 사용자의 순위로 자동 스크롤
    const pageContainer = document.querySelector('.page-container');
    if (winnerList?.infos?.userRank > 3) {
      const userRankLi = document.querySelector(`.prizewinner-list li[data-rank="${winnerList.infos.userRank}"]`);
      if (userRankLi && pageContainer) {
        const liOffsetTop = userRankLi.offsetTop;
        const containerHeight = pageContainer.clientHeight;
        const scrollPosition = liOffsetTop - containerHeight / 2;

        pageContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      }
    } else {
      pageContainer?.scrollTo(0, 0);
    }
  }, [winnerList]);

  useEffect(() => {
    // 스크롤 하면 교환 안내 메시지 제거
    const handleScroll = () => {
      const pageContainer = document.querySelector('.page-container');
      if (pageContainer) {
        const scrollTop = pageContainer.scrollTop;
        setIsNoticeVisible(scrollTop < 48);
      }
    };

    const pageContainer = document.querySelector('.page-container');
    if (pageContainer) {
      pageContainer.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        pageContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  if (!eventInfo || !winnerList) return null;

  return (
    <>
      {winnerList.infos.resultList.length > 0 && (
        <PoolEventPrize
          rank={winnerList.infos.userRank}
          prize={winnerList.infos.userRank > 0 ? winnerList.infos.resultList[winnerList.infos.userRank - 1].prize : ''}
        />
      )}

      <section className="prizewinner">
        <div className="prizewinner-header">
          <ArrowIcon onClick={() => navigate(-1)} />
          <div className="prizewinner-header-period">
            <img
              src={TriangleLeft}
              alt="지난주 기록"
              onClick={() => {
                if (currentRound > 1) {
                  setCurrentRound(currentRound - 1);
                } else {
                  setMessage('지난주 기록이 없어요.');
                }
              }}
            />
            <span>
              {format(eventInfo.infos.eventRoundList[currentRound - 1].beginAt, 'yyyy.MM.dd')} ~{' '}
              {format(eventInfo.infos.eventRoundList[currentRound - 1].endAt, 'yyyy.MM.dd')}
            </span>
            <img
              src={TriangleRight}
              alt="다음주 기록"
              onClick={() => {
                if (currentRound < eventInfo.infos.eventRoundList.length) {
                  setCurrentRound(currentRound + 1);
                } else {
                  setMessage('다음주 기록이 없어요.');
                }
              }}
            />
          </div>
        </div>
        {winnerList.infos.resultList.length > 0 ? (
          <div className="prizewinner-contents">
            <div className="podium-finishers">
              <Podium user={winnerList.infos.resultList[1]} />
              <Podium user={winnerList.infos.resultList[0]} />
              <Podium user={winnerList.infos.resultList[2]} />
            </div>
            <div className={`prizewinner-short-notice ${isNoticeVisible ? 'fade-in' : 'fade-out'}`}>
              <img src={Grid2x2} alt="2x2 그리드" />
              <span>상품은 수영장에서 교환 가능 해요!</span>
            </div>
            <ul className="prizewinner-list">
              {winnerList.infos.resultList.slice(3).map((item) => (
                <li key={item.rank} data-rank={item.rank}>
                  <Finisher user={item} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="prizewinner-empty">
            <img src={NotYetCat} alt="추첨중..." />
            <p className="prizewinner-empty-title">아직 추첨 안했다냥~</p>
            <p className="prizewinner-empty-desc">
              매주 월요일 오후 1시에
              <br />
              추첨 결과를 발표 한다냥!
            </p>
          </div>
        )}
      </section>

      <ToastAlert show={!!message} onClose={setMessage} text={message} />
    </>
  );
}

function Podium({ user }) {
  const crown = useMemo(() => {
    if (user.rank === 1) return Crown1;
    if (user.rank === 2) return Crown2;
    if (user.rank === 3) return Crown3;
  }, [user]);

  return (
    <div className={`podium-finishers-item rank-${user.rank}`}>
      <div className={`shape-rect rank-${user.rank}`}>
        <div className="winner-nickname">{user.nickname}</div>
        <div className="winner-code">{user.refCode}</div>
        <div className="winner-prize">{user.prize}</div>
      </div>
      <div className={`shape-circle rank-${user.rank}`}>
        <img src={user.profileImageUrl} alt={`${user.rank}등`} />
      </div>
      <div className="shape-crown">
        <img src={crown} alt={`${user.rank}등`} />
      </div>
    </div>
  );
}

function Finisher({ user }) {
  return (
    <div className="finisher">
      <div className="finisher-rank">{user.rank}</div>
      <div className="finisher-avatar">
        <img src={user.profileImageUrl} alt={user.nickname} />
      </div>
      <div className="finisher-info">
        <div className="finisher-nickname">{user.nickname}</div>
        <div className="finisher-code">{user.refCode}</div>
      </div>
      <div className="finisher-prize">{user.prize}</div>
    </div>
  );
}
